import axios from "axios";
import { logoutUser } from "./authService";

const apiUrl = process.env.REACT_APP_API_URL;

const api = axios.create({
  baseURL: apiUrl,
  headers: {
    "Content-Type": "application/json",
  },
});

let isRefreshing = false;
let failedRequestsQueue = [];

const processQueue = (error, token = null) => {
  failedRequestsQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedRequestsQueue = [];
};

// Request interceptor
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("access_token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

// Response interceptor
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !originalRequest.skipAuthRefresh &&
      originalRequest.url !== "/accounts/token/refresh/"
    ) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedRequestsQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return api(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const refreshToken = localStorage.getItem("refresh_token");
        if (!refreshToken) throw new Error("No refresh token");

        const response = await api.post(
          "/accounts/token/refresh/",
          { refresh: refreshToken },
          { skipAuthRefresh: true },
        );

        const { access, refresh } = response.data;
        localStorage.setItem("access_token", access);
        if (refresh) localStorage.setItem("refresh_token", refresh);

        originalRequest.headers.Authorization = `Bearer ${access}`;
        processQueue(null, access);
        return api(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError);
        if (!originalRequest._isBackgroundRefresh) {
          await logoutUser();
          window.location.href = "/login";
        }
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  },
);

export const silentRefresh = async () => {
  try {
    const refreshToken = localStorage.getItem("refresh_token");
    if (!refreshToken) return null;

    const response = await api.post(
      "/accounts/token/refresh/",
      { refresh: refreshToken },
      { _isBackgroundRefresh: true, skipAuthRefresh: true },
    );

    localStorage.setItem("access_token", response.data.access);
    if (response.data.refresh) {
      localStorage.setItem("refresh_token", response.data.refresh);
    }
    return response.data;
  } catch (error) {
    console.error("Silent refresh failed:", error);
    return null;
  }
};

// Setup periodic token refresh (every 10 minutes)
const REFRESH_INTERVAL = 10 * 60 * 1000;
let refreshIntervalId = setInterval(silentRefresh, REFRESH_INTERVAL);

export const clearAuthIntervals = () => {
  clearInterval(refreshIntervalId);
};

export default api;
